import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAtomValue, useSetAtom } from 'jotai'
import { GlobalEvents } from '@highlight/ipc'
import * as Sentry from '@sentry/electron/renderer'

import { useAuth } from '@renderer/chat/providers'
import {
  conversationStartPayload,
  isConversationStagedAction,
  START_CHAT_EVENT,
} from '@renderer/chat/integrations/conversation-action'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { stagedActionEventProperties } from '@renderer/overlay/Sidepanel/hooks/staged-actions/track-staged-action'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'
import { FOR_YOU_CHATS_QUERY_KEY } from '@renderer/stores/conversation-query-keys'

import { invokingIdAtom, invokeErrorsAtom, pendingDisplayAtom, undoErrorsAtom } from '../staged-action-local-state'
import { rememberExecution } from './execution-marks'
import { schedulePendingMetadata } from './metadata'
import { useResumeChatAfterInvoke } from './resume-chat'
import { isMockAction, isStagedActionUserEdited, STAGED_ACTIONS_QUERY_KEY } from './utils'
import type { InvokeOptions, StagedAction } from './types'

/**
 * Single canonical invoke flow shared by the in-app button and the
 * `staged-action:invoke` global event listener (external CC sessions).
 */
export function useInvokeStagedAction() {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()
  const setInvokingId = useSetAtom(invokingIdAtom)
  const setInvokeErrors = useSetAtom(invokeErrorsAtom)
  const setUndoErrors = useSetAtom(undoErrorsAtom)
  const setPendingDisplay = useSetAtom(pendingDisplayAtom)
  const resumeChatAfterInvoke = useResumeChatAfterInvoke()

  return useCallback(
    async (id: string, opts: InvokeOptions): Promise<void> => {
      setInvokingId(id)
      setInvokeErrors((prev) => {
        if (!prev.has(id)) return prev
        const next = new Map(prev)
        next.delete(id)
        return next
      })
      setUndoErrors((prev) => {
        if (!prev.has(id)) return prev
        const next = new Map(prev)
        next.delete(id)
        return next
      })
      await queryClient.cancelQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] })
      const previousActions = queryClient.getQueryData<Array<StagedAction>>([STAGED_ACTIONS_QUERY_KEY])
      const priorAction = previousActions?.find((a) => a.id === id)
      const wasEdited = isStagedActionUserEdited(priorAction)

      try {
        const token = await getAuthToken()
        const response = await fetch(getChatApiRoute(`api/v1/staged-actions/${id}/invoke`), {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        })

        if (!response.ok) {
          const errorText = await response.text().catch(() => '')
          let errorMessage = 'Failed to invoke staged action'
          let errorCode = ''
          try {
            const parsed = JSON.parse(errorText)
            errorMessage =
              typeof parsed?.error === 'string'
                ? parsed.error
                : parsed?.error?.message || parsed?.message || errorMessage
            errorCode = typeof parsed?.errorCode === 'string' ? parsed.errorCode : ''
          } catch {
            if (errorText) errorMessage = errorText
          }
          const invokeError = new Error(errorMessage) as Error & { errorCode?: string }
          invokeError.errorCode = errorCode
          throw invokeError
        }

        // A conversation action's work is the chat: open it, unsent, now that the
        // card has recorded the handoff. The sidepanel owns chats, so it opens
        // there whichever window invoked.
        if (priorAction && isConversationStagedAction(priorAction)) {
          const payload = conversationStartPayload(priorAction)
          if (payload) GlobalEvents.emit(START_CHAT_EVENT, { ...payload })
        }

        const updatedResponse = await fetch(getChatApiRoute(`api/v1/staged-actions/${id}?expand=true`), {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
        })
        const updatedAction = updatedResponse.ok ? ((await updatedResponse.json()).data as StagedAction) : null

        AmplitudeRenderer.trackEvent('Staged Action', {
          action: 'invoke success',
          ...(updatedAction && stagedActionEventProperties(updatedAction)),
          actionId: id,
          source: opts.source,
          claudeCodeSessionId: opts.claudeCodeSessionId,
          wasEdited,
        })

        const now = new Date().toISOString()
        // Pin the execution state before publishing it: the server's own
        // `updated` broadcast and any refetch already in flight both answer from
        // before this invoke, and either landing last would walk the row back.
        rememberExecution({
          id,
          lastInvokedAt: updatedAction?.lastInvokedAt ?? priorAction?.lastInvokedAt ?? now,
          outputData: updatedAction?.outputData ?? null,
          resolvedAt: updatedAction?.resolvedAt ?? null,
        })
        if (updatedAction) {
          queryClient.setQueryData<Array<StagedAction>>(
            [STAGED_ACTIONS_QUERY_KEY],
            (old) =>
              old?.map((a) => {
                if (a.id !== id) return a
                return {
                  ...a,
                  ...updatedAction,
                  lastInvokedAt: updatedAction.lastInvokedAt ?? a.lastInvokedAt ?? now,
                }
              }) ?? [],
          )
        } else {
          queryClient.setQueryData<Array<StagedAction>>(
            [STAGED_ACTIONS_QUERY_KEY],
            (old) => old?.map((a) => (a.id === id ? { ...a, lastInvokedAt: a.lastInvokedAt ?? now } : a)) ?? [],
          )
          queryClient.invalidateQueries({ queryKey: [STAGED_ACTIONS_QUERY_KEY] })
        }

        // Anchor the pending window to client-confirmed success (now), not the
        // server's lastInvokedAt. With a short COMPLETED_ACTION_DURATION_MS, the
        // invoke round-trip (POST + expand GET) would otherwise consume most of
        // the window before the user ever sees it, so the row would flash and
        // vanish the instant invoking finishes. Peers receive this same value via
        // the emit below, so cross-window pending stays aligned.
        const startTime = Date.now()
        setPendingDisplay((prev) => {
          const next = new Map(prev)
          next.set(id, startTime)
          return next
        })
        // Released only once the completed state is in place. Clearing it first
        // leaves a frame with nothing to show: the ring is gone because nothing
        // is running, and the badge isn't there yet because nothing has landed.
        setInvokingId(null)
        // pendingDisplay is window-local and shadows the synced cache. Peers
        // receive this invoke's outputData/lastInvokedAt via cache sync but,
        // without a pending marker, would derive "completed" and hide the row
        // under the completed filter while this window shows it pending. Tell
        // peers to seed the same marker. (Counterpart to staged-action:reverted.)
        GlobalEvents.emitToPeers('staged-action:invoked', id, startTime)
        // This window hears it too, for listeners like the setup-call invite; the pending marker is already set here
        GlobalEvents.emitLocally('staged-action:invoked', id, startTime)

        resumeChatAfterInvoke(updatedAction)

        queryClient.setQueryData<Array<StagedAction>>(
          [STAGED_ACTIONS_QUERY_KEY],
          (old) => old?.map((a) => (a.id === id ? { ...a, metadata: { ...a.metadata, readAt: now } } : a)) ?? [],
        )
        if (!isMockAction(id)) {
          schedulePendingMetadata(id, { readAt: now })
        }
        void queryClient
          .invalidateQueries({ queryKey: [FOR_YOU_CHATS_QUERY_KEY] })
          .catch((error) => Sentry.captureException(error))
      } catch (error) {
        setInvokingId(null)
        let errorMessage = error instanceof Error ? error.message : 'Failed to invoke action'
        let errorCode =
          typeof (error as { errorCode?: unknown })?.errorCode === 'string'
            ? (error as { errorCode: string }).errorCode
            : ''
        try {
          const parsed = JSON.parse(errorMessage)
          if (typeof parsed.error === 'string') errorMessage = parsed.error
          if (typeof parsed.errorCode === 'string') errorCode = parsed.errorCode
        } catch {
          // raw errorMessage
        }

        const failedAction = previousActions?.find((a) => a.id === id)
        AmplitudeRenderer.trackEvent('Staged Action', {
          action: 'invoke error',
          ...(failedAction && stagedActionEventProperties(failedAction)),
          actionId: id,
          errorMessage,
          errorCode,
          source: opts.source,
          claudeCodeSessionId: opts.claudeCodeSessionId,
          wasEdited,
        })
        // No rollback: nothing was written optimistically before the request, so
        // restoring `previousActions` here could only replay a pre-invoke
        // snapshot over whatever has landed since (a peer's invoke, a refetch) —
        // the same stale-write this flow guards against on the success path.
        setInvokeErrors((prev) => {
          const next = new Map(prev)
          next.set(id, { errorMessage, errorCode, errorAt: Date.now() })
          return next
        })
      }
    },
    [
      getAuthToken,
      queryClient,
      setInvokingId,
      setInvokeErrors,
      setUndoErrors,
      setPendingDisplay,
      resumeChatAfterInvoke,
    ],
  )
}

/** Button-initiated invoke plus the shared in-flight invoking id. */
export function useStagedActionInvoke() {
  const invokeStagedActionWithSource = useInvokeStagedAction()
  const invokingId = useAtomValue(invokingIdAtom)
  const invokeStagedAction = useCallback(
    (id: string) => invokeStagedActionWithSource(id, { source: 'button' }),
    [invokeStagedActionWithSource],
  )
  return { invokeStagedAction, invokingId }
}
